import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Save } from "lucide-react";

export default function AdminSettings() {
  const { reload } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setS((p) => ({ ...p, social: { ...(p.social || {}), [k]: v } }));
  const setSeo = (k, v) => setS((p) => ({ ...p, seo: { ...(p.seo || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); reload(); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="p-10"><Skeleton className="h-96 rounded-2xl" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <h1 className="font-display text-3xl text-navy">Settings</h1>
      <p className="text-muted-foreground mt-1">Edit contact details, social links and SEO. These reflect across the site.</p>

      <Section title="Brand & Contact">
        <F label="Brand Name" v={s.brand_name} on={(v) => set("brand_name", v)} />
        <F label="Tagline" v={s.tagline} on={(v) => set("tagline", v)} />
        <F label="Logo URL" v={s.logo_url} on={(v) => set("logo_url", v)} testid="set-logo" />
        <F label="Phone" v={s.phone} on={(v) => set("phone", v)} testid="set-phone" />
        <F label="WhatsApp (with country code, e.g. +919800000000)" v={s.whatsapp} on={(v) => set("whatsapp", v)} testid="set-whatsapp" />
        <F label="Email" v={s.email} on={(v) => set("email", v)} testid="set-email" />
        <F label="Office Address" v={s.address} on={(v) => set("address", v)} />
        <F label="RERA Number" v={s.rera_number} on={(v) => set("rera_number", v)} />
        <F label="Business Hours" v={s.business_hours} on={(v) => set("business_hours", v)} />
        <F label="Map Query (for Google Maps)" v={s.map_embed_query} on={(v) => set("map_embed_query", v)} />
      </Section>

      <Section title="Social Links">
        <F label="Instagram URL" v={s.social?.instagram} on={(v) => setSocial("instagram", v)} />
        <F label="Facebook URL" v={s.social?.facebook} on={(v) => setSocial("facebook", v)} />
        <F label="LinkedIn URL" v={s.social?.linkedin} on={(v) => setSocial("linkedin", v)} />
        <F label="YouTube URL" v={s.social?.youtube} on={(v) => setSocial("youtube", v)} />
      </Section>

      <Section title="Analytics & Integrations">
        <F label="Google Analytics Measurement ID (e.g. G-XXXXXXX)" v={s.ga_measurement_id} on={(v) => set("ga_measurement_id", v)} />
        <F label="Meta Pixel ID" v={s.meta_pixel_id} on={(v) => set("meta_pixel_id", v)} />
        <p className="text-xs text-muted-foreground">CRM webhook is configured via the backend CRM_WEBHOOK_URL environment variable (keeps your credentials private).</p>
      </Section>

      <Section title="SEO (Home Page)">
        <F label="Home SEO Title" v={s.seo?.home_title} on={(v) => setSeo("home_title", v)} />
        <div><Label className="text-xs font-semibold text-navy">Home SEO Description</Label><Textarea value={s.seo?.home_description || ""} onChange={(e) => setSeo("home_description", e.target.value)} className="bg-gray-50 mt-1" rows={2} /></div>
      </Section>

      <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="mt-8 bg-gold text-navy hover:bg-gold-dark font-semibold"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Settings"}</Button>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-card p-6 space-y-4">
    <h2 className="font-display text-xl text-navy">{title}</h2>{children}
  </div>
);
const F = ({ label, v, on, testid }) => (
  <div><Label className="text-xs font-semibold text-navy">{label}</Label><Input value={v || ""} onChange={(e) => on(e.target.value)} className="bg-gray-50 mt-1" data-testid={testid} /></div>
);
